import React from "react";
import { BookmarkIcon } from "@heroicons/react/24/solid/index.js";
import { useParams } from "react-router-dom";
import { useQueryGetExamPaperStatus } from "@/apis/exam_progress/getExamPaperStatus";
import { getPaperTypeLabel } from "@/constants/exam";

interface ReviewPanelProps {
  questions: any[];
  answeredQuestions: number[];
  forReviewQuestions: number[];
  onQuestionSelect: (idx: number) => void;
}

const ReviewPanel: React.FC<ReviewPanelProps> = ({
  questions,
  answeredQuestions,
  forReviewQuestions,
  onQuestionSelect,
}) => {
  const { id, exam_paper_id } = useParams<{
    id: string;
    exam_paper_id: string;
  }>();
  const { data: progressStatus } = useQueryGetExamPaperStatus(
    id || "",
    exam_paper_id || ""
  );

  const sectionTitle =
    progressStatus?.paper?.exam_type === "SAT"
      ? `${getPaperTypeLabel(
          (progressStatus as any)?.paper?.subject
        )}, Module ${(progressStatus as any)?.paper?.current_module || 1}`
      : getPaperTypeLabel((progressStatus as any)?.paper?.subject);

  return (
    <div className="flex flex-col items-start justify-start pt-12 w-full">
      <h1 className="text-3xl mb-4 text-center w-full">Check Your Work</h1>
      <p className="text-lg text-gray-700 mb-2 text-center max-w-2xl w-full mx-auto">
        On test day, you won't be able to move on to the next module until time
        expires.
        <br />
        For these practice questions, you can click <b>Next</b> when you're
        ready to move on.
      </p>
      <div className="bg-white rounded-xl shadow-lg px-10 py-8 mt-8 flex flex-col items-center w-full max-w-3xl mx-auto">
        <div className="flex items-center w-full mb-4">
          <span className="font-bold text-lg flex-1 capitalize">
            {sectionTitle}
          </span>
          <span className="flex items-center text-sm mr-4">
            <span className="inline-block w-4 h-4 border border-black border-dashed rounded-none mr-1" />{" "}
            Unanswered
          </span>
          <span className="flex items-center text-sm">
            <BookmarkIcon className="w-3 h-3 text-red-500 mr-1" /> For Review
          </span>
        </div>
        <hr className="w-full mb-6" />
        <div className="flex gap-4 flex-wrap justify-center">
          {questions.map((q, idx) => {
            const isAnswered = answeredQuestions.includes(idx);
            const isForReview = forReviewQuestions.includes(idx);
            return (
              <button
                key={idx}
                className={`w-10 h-10 border rounded-none text-lg font-bold flex items-center justify-center transition-all cursor-pointer relative
                  ${
                    isAnswered
                      ? "bg-[#3451d6] text-white border-[#3451d6] border-solid hover:opacity-80"
                      : "bg-white text-[#3451d6] border-black border-dashed hover:bg-blue-50"
                  }`}
                onClick={() => onQuestionSelect(idx)}
              >
                {isForReview && (
                  <div className="absolute -top-2 -right-2 bg-white rounded-full p-0.5">
                    <BookmarkIcon className="w-3 h-3 text-red-500" />
                  </div>
                )}
                {idx + 1}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ReviewPanel;
